import { useReducer, useCallback, useRef } from 'react';
import { buildCommandRegistry } from '../commands/commandRegistry';
import { parseCommand, getAutocomplete } from '../commands/commandParser';
import { generateHelp } from '../commands/commandRegistry';
import { personalInfo } from '../data/portfolio';

// ─── State ────────────────────────────────────────────────────────────────────
const initialState = {
  input: '',
  history: [],       // [{ type: 'input'|'output', text, isError }]
  cmdHistory: [],    // previous raw commands for ↑↓ navigation
  historyIndex: -1,  // -1 = current input
  suggestions: [],
  suggestionIndex: -1,
  activeFilter: null,     // null | category string
  openProjectId: null,    // null | project id
};

function reducer(state, action) {
  switch (action.type) {
    case 'SET_INPUT':
      return { ...state, input: action.payload, historyIndex: -1, suggestionIndex: -1 };
    case 'SET_SUGGESTIONS':
      return { ...state, suggestions: action.payload, suggestionIndex: -1 };
    case 'SET_SUGGESTION_INDEX':
      return { ...state, suggestionIndex: action.payload };
    case 'APPEND_HISTORY':
      return { ...state, history: [...state.history, action.payload] };
    case 'CLEAR_OUTPUT':
      return { ...state, history: [] };
    case 'ADD_CMD_HISTORY':
      return {
        ...state,
        cmdHistory: [action.payload, ...state.cmdHistory].slice(0, 50),
        historyIndex: -1,
      };
    case 'SET_HISTORY_INDEX':
      return { ...state, historyIndex: action.payload };
    case 'SET_INPUT_FROM_HISTORY': {
      const idx = action.payload;
      const input = idx === -1 ? '' : (state.cmdHistory[idx] ?? '');
      return { ...state, historyIndex: idx, input };
    }
    case 'SET_FILTER':
      return { ...state, activeFilter: action.payload };
    case 'OPEN_PROJECT':
      return { ...state, openProjectId: action.payload };
    case 'CLOSE_PROJECT':
      return { ...state, openProjectId: null };
    default:
      return state;
  }
}

// ─── Hook ─────────────────────────────────────────────────────────────────────
export function useCommand({ sectionRefs }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const registryRef = useRef(null);

  // Build handlers that the registry needs
  const handlers = useCallback(() => {
    return {
      scrollTo: (sectionId) => {
        const el = sectionRefs?.current?.[sectionId];
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      },
      openProject: (id) => {
        dispatch({ type: 'OPEN_PROJECT', payload: id });
      },
      filterProjects: (category) => {
        dispatch({ type: 'SET_FILTER', payload: category });
      },
      clearOutput: () => {
        dispatch({ type: 'CLEAR_OUTPUT' });
      },
      downloadResume: () => {
        if (personalInfo.resume) {
          window.open(personalInfo.resume, '_blank');
        } else {
          return 'RESUME NOT YET AVAILABLE\nCheck back soon or contact me directly.';
        }
      },
      showHelp: (registry) => {
        return generateHelp(registry);
      },
    };
  }, [sectionRefs]);

  // Lazy-init registry
  const getRegistry = useCallback(() => {
    if (!registryRef.current) {
      registryRef.current = buildCommandRegistry(handlers());
    }
    return registryRef.current;
  }, [handlers]);

  // ── Execute a command ───────────────────────────────────
  const execute = useCallback((rawInput) => {
    const trimmed = rawInput.trim();
    if (!trimmed) return;

    const registry = getRegistry();

    // Add to output history as input line
    dispatch({ type: 'APPEND_HISTORY', payload: { type: 'input', text: trimmed } });
    dispatch({ type: 'ADD_CMD_HISTORY', payload: trimmed });
    dispatch({ type: 'SET_INPUT', payload: '' });
    dispatch({ type: 'SET_SUGGESTIONS', payload: [] });

    // Parse command
    const matched = parseCommand(trimmed, registry);

    if (!matched) {
      dispatch({
        type: 'APPEND_HISTORY',
        payload: {
          type: 'output',
          text: `Command not found: "${trimmed}"\nType "help" to see available commands.`,
          isError: true,
        },
      });
      return;
    }

    // Execute handler
    const result = matched.handler(trimmed, registry);

    // If handler returned a string, show it as output
    if (typeof result === 'string') {
      dispatch({
        type: 'APPEND_HISTORY',
        payload: { type: 'output', text: result },
      });
    } else {
      // Show a brief confirmation for navigation commands
      const confirmationMap = {
        home: '↑ Scrolling to top...',
        about: '→ Navigating to About...',
        projects: '→ Navigating to Projects...',
        experience: '→ Navigating to Experience...',
        skills: '→ Navigating to Stack...',
        contact: '→ Navigating to Contact...',
        'filter-ai': 'Filtered: AI / ML projects',
        'filter-fullstack': 'Filtered: Full Stack projects',
        'filter-backend': 'Filtered: Backend projects',
        'filter-cloud': 'Filtered: Cloud projects',
        clear: null,
        resume: personalInfo.resume
          ? '↓ Opening resume...'
          : 'Resume not yet available. Contact me directly.',
      };

      if (matched.id.startsWith('open-')) {
        dispatch({
          type: 'APPEND_HISTORY',
          payload: { type: 'output', text: `Opening project...` },
        });
      } else if (matched.id in confirmationMap && confirmationMap[matched.id] !== null) {
        dispatch({
          type: 'APPEND_HISTORY',
          payload: { type: 'output', text: confirmationMap[matched.id] },
        });
      }
    }
  }, [getRegistry]);

  // ── Handle input change (with autocomplete) ─────────────
  const handleInputChange = useCallback((value) => {
    dispatch({ type: 'SET_INPUT', payload: value });
    const registry = getRegistry();
    const suggestions = getAutocomplete(value, registry);
    dispatch({ type: 'SET_SUGGESTIONS', payload: suggestions });
  }, [getRegistry]);

  // ── Keyboard navigation ──────────────────────────────────
  const handleKeyDown = useCallback((e) => {
    const { key } = e;

    if (key === 'ArrowUp') {
      e.preventDefault();
      // Navigate suggestions if open
      if (state.suggestions.length > 0) {
        const newIdx = Math.min(state.suggestionIndex + 1, state.suggestions.length - 1);
        dispatch({ type: 'SET_SUGGESTION_INDEX', payload: newIdx });
        dispatch({ type: 'SET_INPUT', payload: state.suggestions[newIdx] });
      } else {
        // Navigate cmd history
        const newIdx = Math.min(state.historyIndex + 1, state.cmdHistory.length - 1);
        dispatch({ type: 'SET_INPUT_FROM_HISTORY', payload: newIdx });
      }
      return;
    }

    if (key === 'ArrowDown') {
      e.preventDefault();
      if (state.suggestions.length > 0) {
        const newIdx = Math.max(state.suggestionIndex - 1, -1);
        dispatch({ type: 'SET_SUGGESTION_INDEX', payload: newIdx });
        if (newIdx === -1) {
          dispatch({ type: 'SET_INPUT', payload: '' });
        } else {
          dispatch({ type: 'SET_INPUT', payload: state.suggestions[newIdx] });
        }
      } else {
        const newIdx = Math.max(state.historyIndex - 1, -1);
        dispatch({ type: 'SET_INPUT_FROM_HISTORY', payload: newIdx });
      }
      return;
    }

    if (key === 'Tab') {
      e.preventDefault();
      if (state.suggestions.length > 0) {
        const pick = state.suggestions[Math.max(state.suggestionIndex, 0)];
        dispatch({ type: 'SET_INPUT', payload: pick });
        dispatch({ type: 'SET_SUGGESTIONS', payload: [] });
      }
      return;
    }

    if (key === 'Escape') {
      dispatch({ type: 'SET_SUGGESTIONS', payload: [] });
      dispatch({ type: 'CLOSE_PROJECT' });
      return;
    }

    if (key === 'Enter') {
      execute(state.input);
    }
  }, [state, execute]);

  const acceptSuggestion = useCallback((suggestion) => {
    dispatch({ type: 'SET_INPUT', payload: suggestion });
    dispatch({ type: 'SET_SUGGESTIONS', payload: [] });
  }, []);

  const closeProject = useCallback(() => {
    dispatch({ type: 'CLOSE_PROJECT' });
  }, []);

  return {
    input: state.input,
    history: state.history,
    suggestions: state.suggestions,
    suggestionIndex: state.suggestionIndex,
    activeFilter: state.activeFilter,
    openProjectId: state.openProjectId,
    handleInputChange,
    handleKeyDown,
    execute,
    acceptSuggestion,
    closeProject,
  };
}
